import { AbsenceReasonPolicy } from 'constants/AbsenceReasonPolicy';
import {
  ABSENCE_REASON__CONCURRENT_CHANGE,
  ABSENCE_REASON__DEACTIVATED,
  ABSENCE_REASON__INVALID_REMUN_CODE,
  ABSENCE_REASON__NAME_REQUIRED,
  ABSENCE_REASON__NAME_TOO_LONG,
  ABSENCE_REASON__NO_ACCESS,
  ABSENCE_REASON__NOT_FOUND,
  ABSENCE_REASON__REMUN_CODE_DESCRIPTION_REQUIRED,
  ABSENCE_REASON__REMUN_CODE_DESCRIPTION_TOO_LONG,
  ABSENCE_REASON__REMUN_CODE_REQUIRED,
} from 'translations/constants';
import { IAbsenceReason, IAbsenceReasonRequest, ParsedAbsenceReasonError } from 'types/absence-reason/IAbsenceReason';
import { getReasonName } from 'utils/absence-reason';

export interface AbsenceReasonFormValues {
  nameNl: string;
  nameFr: string;
  nameEn: string;
  remunCodeDescription: string;
  /** String because UPInput values are strings; '' means empty (also for an old non-numeric code). */
  remunCode: string;
  remarkPolicy: AbsenceReasonPolicy;
  proofPolicy: AbsenceReasonPolicy;
}

export type AbsenceReasonFormField = keyof AbsenceReasonFormValues;

export const EMPTY_FORM_VALUES: AbsenceReasonFormValues = {
  nameNl: '',
  nameFr: '',
  nameEn: '',
  remunCodeDescription: '',
  remunCode: '',
  remarkPolicy: AbsenceReasonPolicy.OPTIONAL,
  proofPolicy: AbsenceReasonPolicy.OPTIONAL,
};

/** A whole number greater than 0, typed as digits only (no sign, decimals or exponent). */
export const isValidRemunCode = (value: string): boolean => /^\d+$/.test(value.trim()) && Number(value) > 0;

export const toFormValues = (reason: IAbsenceReason): AbsenceReasonFormValues => ({
  nameNl: reason.nameNl,
  nameFr: reason.nameFr,
  nameEn: reason.nameEn,
  remunCodeDescription: reason.remunCodeDescription,
  remunCode: reason.remunCode === null ? '' : String(reason.remunCode),
  remarkPolicy: reason.remarkPolicy,
  proofPolicy: reason.proofPolicy,
});

/** Names and description are trimmed server-side, so they are sent as typed. */
export const toRequest = (values: AbsenceReasonFormValues): IAbsenceReasonRequest => ({
  nameNl: values.nameNl,
  nameFr: values.nameFr,
  nameEn: values.nameEn,
  remunCodeDescription: values.remunCodeDescription,
  remunCode: Number(values.remunCode),
  remarkPolicy: values.remarkPolicy,
  proofPolicy: values.proofPolicy,
});

export interface ServerFieldError {
  field: AbsenceReasonFormField;
  key: string;
}

const NAME_FIELD_BY_LANGUAGE: Record<string, AbsenceReasonFormField> = { nl: 'nameNl', fr: 'nameFr', en: 'nameEn' };

/** Maps a 400 validation error to the form field it belongs to, so it can be shown inline. */
export const getServerFieldError = (parsed: ParsedAbsenceReasonError): ServerFieldError | null => {
  switch (parsed.key) {
    case ABSENCE_REASON__NAME_REQUIRED:
    case ABSENCE_REASON__NAME_TOO_LONG: {
      const field = NAME_FIELD_BY_LANGUAGE[String(parsed.details.language ?? '')];
      return field ? { field, key: parsed.key } : null;
    }
    case ABSENCE_REASON__REMUN_CODE_DESCRIPTION_REQUIRED:
    case ABSENCE_REASON__REMUN_CODE_DESCRIPTION_TOO_LONG:
      return { field: 'remunCodeDescription', key: parsed.key };
    case ABSENCE_REASON__REMUN_CODE_REQUIRED:
    case ABSENCE_REASON__INVALID_REMUN_CODE:
      return { field: 'remunCode', key: parsed.key };
    default:
      return null;
  }
};

const HANDLED_KEYS = [ABSENCE_REASON__NOT_FOUND, ABSENCE_REASON__DEACTIVATED, ABSENCE_REASON__CONCURRENT_CHANGE];

/** INVALID_POLICY / ROW_VERSION_REQUIRED / ModelState 400s are client bugs and get the generic fallback. */
export const getAbsenceReasonErrorKey = (parsed: ParsedAbsenceReasonError, fallbackKey: string): string => {
  if (parsed.key && HANDLED_KEYS.includes(parsed.key)) return parsed.key;
  if (parsed.status === 403) return ABSENCE_REASON__NO_ACCESS;
  return fallbackKey;
};

const normalise = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

export const filterAbsenceReasons = (
  rows: IAbsenceReason[],
  searchTerm: string,
  language: string,
): IAbsenceReason[] => {
  const term = normalise(searchTerm);
  if (!term) return rows;
  return rows.filter(row =>
    [getReasonName(row, language), row.remunCodeDescription, row.remunCode?.toString() ?? ''].some(value =>
      normalise(value).includes(term),
    ),
  );
};
